import { Link as NextLink } from '@/navigation'
import { twMerge } from 'tailwind-merge'
import { ComponentPropsWithRef, ReactNode, MouseEvent } from 'react'

/**
 * Merged with tailwind-merge, not concatenated. Two thirds of the call sites pass
 * `no-underline` to cancel the default variant's `underline`, and with a plain
 * concatenation both classes survive, so which one applied came down to their order
 * in the stylesheet. Tailwind 4 reorders them, which turned that into 15 underlined
 * links overnight.
 */
const styles = {
    default:
        'text-textLink text-base font-medium underline underline-offset-[3px] transition-all duration-300 border-b-borderPositive hover:no-underline',
    plain: ''
}

export type LinkProps = ComponentPropsWithRef<typeof NextLink> & {
    children: ReactNode
    className?: string
    onClick?: (event: MouseEvent<HTMLAnchorElement>) => void
    variant?: 'default' | 'plain'
}

const Link = ({ children, className, onClick, variant = 'default', ...props }: LinkProps) => {
    /**
     * Custom click handler that combines scroll-to-top functionality with the original onClick behavior.
     *
     * We need this because:
     * 1. The Next.js Link component's default scroll-to-top behavior doesn't work with custom scroll containers
     *    that use overflow-y-auto (like our RootLayout component)
     * 2. We want to preserve any onClick handlers that users might pass to our Link component
     *
     * This function:
     * 1. Finds the main content container and scrolls it to the top
     * 2. Calls the original onClick handler if one was provided
     *
     * @param e - The mouse event from clicking the link
     */
    const scrollContainerToTop = (e: MouseEvent<HTMLAnchorElement>) => {
        const mainContainer = document.querySelector('main')
        if (mainContainer) {
            mainContainer.scrollTop = 0
        }
        onClick?.(e)
    }

    return (
        <NextLink role="link" onClick={scrollContainerToTop} className={twMerge(styles[variant], className)} {...props}>
            {children}
        </NextLink>
    )
}

export default Link
